import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { RetailPrice } from '../utils/types';

/**
 * Reads retail_prices from the DB. The price_usd_per_kg column is already
 * computed by the ingest edge function at scrape time using the FX rate of
 * that day. The frontend just reads it — no FX logic needed here.
 */

interface Params {
  speciesId: number | null;
  targetSkuIds?: number[];
  countries?: string[];
  retailers?: string[];
  tiers?: string[];
  dateFrom?: Date;
  dateTo?: Date;
}

const EMPTY_PRICES: RetailPrice[] = [];

export function useRetailPrices(params: Params) {
  const [result, setResult] = useState<{
    key: string;
    data: RetailPrice[];
    error: string | null;
  } | null>(null);

  // Stable deps keys to avoid re-running on identical arrays
  const countriesKey = (params.countries ?? []).join(',');
  const retailersKey = (params.retailers ?? []).join(',');
  const tiersKey = (params.tiers ?? []).join(',');
  const skuIdsKey = (params.targetSkuIds ?? []).join(',');
  const fromKey = params.dateFrom?.getTime() ?? 0;
  const toKey = params.dateTo?.getTime() ?? 0;
  const requestKey = JSON.stringify([
    params.speciesId,
    countriesKey,
    retailersKey,
    tiersKey,
    skuIdsKey,
    fromKey,
    toKey,
  ]);

  useEffect(() => {
    if (!params.speciesId) {
      setResult(null);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        let q = supabase
          .from('retail_prices')
          .select('*')
          .eq('species_id', params.speciesId!)
          .order('captured_at', { ascending: true });

        if (params.countries?.length) q = q.in('country', params.countries);
        if (params.retailers?.length) q = q.in('retailer', params.retailers);
        if (params.tiers?.length) q = q.in('tier', params.tiers);
        if (params.targetSkuIds?.length) {
          q = q.in('target_sku_id', params.targetSkuIds);
        }
        if (params.dateFrom) q = q.gte('captured_at', params.dateFrom.toISOString());
        if (params.dateTo) q = q.lte('captured_at', params.dateTo.toISOString());

        const { data: rows, error } = await q;
        if (error) throw error;
        if (cancelled) return;
        setResult({
          key: requestKey,
          data: (rows ?? []) as RetailPrice[],
          error: null,
        });
      } catch (e: any) {
        if (!cancelled) {
          setResult({
            key: requestKey,
            data: EMPTY_PRICES,
            error: e.message ?? String(e),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  // A new selection is loading from its first render, before the effect runs.
  const currentResult =
    params.speciesId && result?.key === requestKey ? result : null;
  return {
    data: currentResult?.data ?? EMPTY_PRICES,
    loading: Boolean(params.speciesId) && !currentResult,
    error: currentResult?.error ?? null,
  };
}
